/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet-async';
import { v4 } from 'uuid';
import { useNavigate, useParams } from 'react-router-dom';
import usePrompt from 'hooks/usePrompt';

import {
  IProfile,
  STORE_LOAD_FRESH,
  useStore,
} from '@mcn-platform/models';
import ItemEdit from 'components/item/ItemEdit';
import EditorBreadcrumbs from 'components/editor/EditorBreadcrumbs';
import ButtonStack from 'components/item/ButtonStack';

const ProfileEditor = () => {
  const store = useStore();
  const [profile, setProfile] = useState<IProfile>();
  const { key } = useParams();
  const navigate = useNavigate();

  usePrompt({ isDirty: profile ? (profile.changeCount > 0) : false });

  useEffect(() => {
    if (!key) {
      return;
    }
    if (key === 'new') {
      if (profile) return;
      const orgId = store.currentOrganization?.id;
      if (!orgId) return;
      const newProfile = store.create(
        'profile',
        v4(),
        'New Profile',
        {
          organizationID: orgId,
        },
      );
      setProfile(newProfile);
    } else {
      const loadedProfile = store.getOrLoad('profile', key, STORE_LOAD_FRESH);
      setProfile(loadedProfile);
    }
    // Do not depend on profile / syncState — getOrLoad(STORE_LOAD_FRESH) would loop when load completes.
  }, [store, key]);

  const saveProfile = () => {
    if (!profile) return;
    if (key === 'new') {
      store.addStub('profile', profile).then(() => {
        navigate(`/app/profile/${profile.organizationID}~${profile.id}`);
      });
    } else {
      store.update('profile', profile);
    }
  };

  if (!profile) {
    return <div>Loading.</div>;
  }
  if (profile.syncState === 'error') {
    return <div>Error loading profile</div>;
  }
  if (profile.syncState === 'saving') {
    return <div>Saving..</div>;
  }
  if (key !== 'new' && profile.syncState !== 'loaded') {
    return <div>Loading..</div>;
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
    >
      <Helmet>
        <title>{key === 'new' ? 'New Profile' : 'Edit Profile'}</title>
      </Helmet>
      <EditorBreadcrumbs
        currentLabel={profile.name?.trim() || (key === 'new' ? 'New profile' : 'Profile')}
      />
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: '100%',
      }}
      >
        <Box sx={{ flex: '1', overflowY: 'auto' }}>
          <ItemEdit
            item={profile}
            modelName="profile"
            handleSubmit={(newValues) => {
              profile.updateValues(newValues);
            }}
            hideTitle
            autoSave
          />
        </Box>
        <Box sx={{ flex: 0 }}>
          <ButtonStack>
            <Button
              color="primary"
              variant="contained"
              onClick={saveProfile}
              disabled={profile.changeCount === 0}
            >
              Save
            </Button>
          </ButtonStack>
        </Box>
      </Box>
    </Box>
  );
};

export default observer(ProfileEditor);
